import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { firmsAction } from 'common/actions';
import { MEASUREMENT_DATES_BY_COMPANY_QUERY_KEY } from 'common/constants/services/companies';
import { MEASUREMENT_DATES_BY_FIRM_QUERY_KEY } from 'common/constants/services/firms';
import { useStore } from 'common/store';
import { MeasurementDate } from 'common/types/measurementDate';
import { UseStoreValues } from 'common/types/store';
import { getMeasurementDatesByCompany } from 'services/hooks/companies';
import {
  RefreshFirmMeasurementDatesParams,
  UseRefreshFirmMeasurementDatesReturn,
} from './useRefreshFirmMeasurementDates.types';

const useRefreshFirmMeasurementDates = (): UseRefreshFirmMeasurementDatesReturn => {
  const [{ firmId }, dispatch] = useStore() as UseStoreValues;
  const queryClient = useQueryClient();

  const refreshFirmMeasurementDates = useCallback(
    async ({ companyId }: RefreshFirmMeasurementDatesParams) => {
      // Keep Prism selectors query-backed while syncing legacy store consumers until cleanup removes them.
      await queryClient.invalidateQueries({
        queryKey: [MEASUREMENT_DATES_BY_COMPANY_QUERY_KEY, companyId],
      });

      const companyMeasurementDates = await queryClient.fetchQuery({
        queryFn: () => getMeasurementDatesByCompany({ companyId }),
        queryKey: [MEASUREMENT_DATES_BY_COMPANY_QUERY_KEY, companyId],
      });

      dispatch(firmsAction.setCompanyMeasurementDateList((companyMeasurementDates ?? []) as MeasurementDate[]));

      if (!firmId) return;

      await queryClient.invalidateQueries({
        queryKey: [MEASUREMENT_DATES_BY_FIRM_QUERY_KEY, firmId],
      });
    },
    [dispatch, firmId, queryClient]
  );

  return {
    refreshFirmMeasurementDates,
  };
};

export default useRefreshFirmMeasurementDates;
